import { Navigate, Route, Routes } from 'react-router-dom';
import { Permission } from '@metron/shared';
import { ProtectedRoute } from './components/ProtectedRoute.tsx';
import { DashboardLayout } from './components/DashboardLayout.tsx';
import { DashboardPage } from './pages/DashboardPage.tsx';
import { CallbackPage } from './pages/CallbackPage.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { UsersPage } from './pages/UsersPage.tsx';
import { WellspringAccessPage } from './pages/WellspringAccessPage.tsx';
import { OnboardingQueuePage } from './pages/OnboardingQueuePage.tsx';
import { CustomersPage } from './pages/CustomersPage.tsx';
import { CustomerDetailsPage, CustomerTabRedirect } from './pages/CustomerDetailsPage.tsx';
import { DataLinkAccountsPage } from './pages/DataLinkAccountsPage.tsx';
import { AuditLogsPage } from './pages/AuditLogsPage.tsx';
import { PropertyUploadsPage } from './pages/PropertyUploadsPage.tsx';
import { CustomGroupsPage } from './pages/CustomGroupsPage.tsx';
import { IngestStatisticsPage } from './pages/IngestStatisticsPage.tsx';
import { UdpLogsPage } from './pages/UdpLogsPage.tsx';
import { DuplicateMessagesPage } from './pages/DuplicateMessagesPage.tsx';
import { MaxFlowPage } from './pages/MaxFlowPage.tsx';
import { WaterSavedPage } from './pages/WaterSavedPage.tsx';
import { MostNegativeAvgUsePage } from './pages/MostNegativeAvgUsePage.tsx';
import { LargestClassifiedBackflowPage } from './pages/LargestClassifiedBackflowPage.tsx';
import { ReadRateSlaByCustomerPage } from './pages/ReadRateSlaByCustomerPage.tsx';
import { ReadRateSlaByProductTypePage } from './pages/ReadRateSlaByProductTypePage.tsx';
import { ActivationStatusPage } from './pages/ActivationStatusPage.tsx';
import { FeatureFlagsPage } from './pages/FeatureFlagsPage.tsx';
import { AlertOverviewPage } from './pages/AlertOverviewPage.tsx';
import { NotificationOverviewPage } from './pages/NotificationOverviewPage.tsx';
import { MeterInspectorPage } from './pages/MeterInspectorPage.tsx';
import { MeterDetailPage } from './pages/MeterDetailPage.tsx';
import { MeterSearchPage } from './pages/MeterSearchPage.tsx';
import { CustomerSearchPage } from './pages/CustomerSearchPage.tsx';
import { UserSearchPage } from './pages/UserSearchPage.tsx';
import { PropertySearchPage } from './pages/PropertySearchPage.tsx';
import { CustomGroupSearchPage } from './pages/CustomGroupSearchPage.tsx';
import { EndConsumerSearchPage } from './pages/EndConsumerSearchPage.tsx';
import { ReportsPage } from './pages/ReportsPage.tsx';
import { DocsIndexPage } from './pages/DocsIndexPage.tsx';
import { DocPage } from './pages/DocPage.tsx';
import { CallInCadenceReportPage } from './pages/CallInCadenceReportPage.tsx';
import { AdminRefreshPage } from './pages/AdminRefreshPage.tsx';
import { RequireAdmin } from './components/RequireAdmin.tsx';
import { Toaster } from './components/ui/sonner.tsx';

export function App() {
  return (
    <>
      <Routes>
        <Route path='/login' element={<LoginPage />} />
        <Route path='/callback' element={<CallbackPage />} />
        <Route
          path='/'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_DASHBOARD}>
              <DashboardLayout>
                <DashboardPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/alert-overview'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <AlertOverviewPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/notification-overview'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <NotificationOverviewPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/read-rate-sla/by-customer'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <ReadRateSlaByCustomerPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/read-rate-sla/by-product-type'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <ReadRateSlaByProductTypePage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/ingest-statistics'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <IngestStatisticsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/udp-logs'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <UdpLogsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/duplicate-messages'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <DuplicateMessagesPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/max-flow'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <MaxFlowPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/water-saved'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <WaterSavedPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/backflow/most-negative-avg-use'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <MostNegativeAvgUsePage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/backflow/largest-classified'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METRICS}>
              <DashboardLayout>
                <LargestClassifiedBackflowPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/users'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_USERS}>
              <DashboardLayout>
                <UsersPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/wellspring-access'
          element={
            <ProtectedRoute requiredPermission={Permission.MANAGE_WELLSPRING_ROLES}>
              <DashboardLayout>
                <WellspringAccessPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/customer-onboarding-queue'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_ONBOARDING_QUEUE}>
              <DashboardLayout>
                <OnboardingQueuePage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/customers'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_CUSTOMERS}>
              <DashboardLayout>
                <CustomersPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/customer-activation-status'
          element={
            <ProtectedRoute requiredPermission={Permission.MANAGE_CUSTOMER_ASSIGNMENTS}>
              <DashboardLayout>
                <ActivationStatusPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/customers/:customerId'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_CUSTOMERS}>
              <DashboardLayout>
                <CustomerDetailsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/customers/:customerId/properties'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_CUSTOMERS}>
              <CustomerTabRedirect tab='properties' />
            </ProtectedRoute>
          }
        />
        <Route
          path='/customers/:customerId/end-consumers'
          element={
            <ProtectedRoute requiredPermission={Permission.MANAGE_END_CONSUMERS}>
              <CustomerTabRedirect tab='consumers' />
            </ProtectedRoute>
          }
        />
        <Route
          path='/datalink/accounts'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_DATALINK_ACCOUNTS}>
              <DashboardLayout>
                <DataLinkAccountsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/audit-logs'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_AUDIT_LOGS}>
              <DashboardLayout>
                <AuditLogsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/property-uploads'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_ALL_PROPERTY_UPLOADS}>
              <DashboardLayout>
                <PropertyUploadsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/custom-groups'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_CUSTOM_GROUPS}>
              <DashboardLayout>
                <CustomGroupsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/meter-inspector'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METER_INSPECTOR}>
              <DashboardLayout>
                <MeterInspectorPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/search/meters'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METER_INSPECTOR}>
              <DashboardLayout>
                <MeterSearchPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/search/users'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_USERS}>
              <DashboardLayout>
                <UserSearchPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/search/customers'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_CUSTOMERS}>
              <DashboardLayout>
                <CustomerSearchPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/search/properties'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_CUSTOMERS}>
              <DashboardLayout>
                <PropertySearchPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/search/custom-groups'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_CUSTOM_GROUPS}>
              <DashboardLayout>
                <CustomGroupSearchPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/search/end-consumers'
          element={
            <ProtectedRoute requiredPermission={Permission.MANAGE_END_CONSUMERS}>
              <DashboardLayout>
                <EndConsumerSearchPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/meter-inspector/:sensorId'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METER_INSPECTOR}>
              <DashboardLayout>
                <MeterDetailPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/docs'
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <DocsIndexPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/docs/:slug'
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <DocPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/reports'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METER_INSPECTOR}>
              <DashboardLayout>
                <ReportsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/reports/call-in-cadence'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_METER_INSPECTOR}>
              <DashboardLayout>
                <CallInCadenceReportPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/feature-flags'
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_FEATURE_FLAGS}>
              <DashboardLayout>
                <FeatureFlagsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path='/admin/metrics/refresh'
          element={
            <ProtectedRoute>
              <RequireAdmin>
                <DashboardLayout>
                  <AdminRefreshPage />
                </DashboardLayout>
              </RequireAdmin>
            </ProtectedRoute>
          }
        />
        {/* Old path, kept so bookmarks still work. */}
        <Route path='/admin/refresh' element={<Navigate to='/admin/metrics/refresh' replace />} />
        <Route
          path='*'
          element={
            <div className='min-h-screen bg-background flex items-center justify-center'>
              <div className='text-center'>
                <h1 className='text-2xl font-bold'>404 - Page not found</h1>
                <p className='text-muted-foreground mt-2'>
                  The page you're looking for doesn't exist.
                </p>
              </div>
            </div>
          }
        />
      </Routes>
      <Toaster />
    </>
  );
}
